import { Loader2 } from 'lucide-react'
import { ComponentType, lazy, Suspense } from 'react'

// Cache para componentes já carregados (componente real, resolvido)
const componentCache = new Map<string, ComponentType<any>>()

// Cache para promessas de carregamento (dedupe de prefetch)
const loadingCache = new Map<string, Promise<ComponentType<any>>>()

// Cache dos wrappers React.lazy() por nome — precisa manter a MESMA
// referência entre renders. Criar um lazy() novo a cada render faz o React
// tratá-lo como um tipo de elemento diferente, remontando a árvore e
// quebrando o Suspense (sintoma: "Cannot read properties of null
// (reading 'useContext')" nos hooks do componente lazy-carregado).
const lazyComponentCache = new Map<string, ComponentType<any>>()

// Resolve depois do evento 'load' + 2s (prefetch não pode competir com a página atual).
function esperarPaginaCarregada(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  return new Promise((resolve) => {
    const depois = () => setTimeout(resolve, 2000)
    if (document.readyState === 'complete') depois()
    else window.addEventListener('load', depois, { once: true })
  })
}

// Interface para configuração de lazy loading
export interface LazyLoadConfig {
  name: string
  importFn: () => Promise<{ default: ComponentType<any> }>
  priority?: 'high' | 'medium' | 'low'
  preload?: boolean
  fallback?: ComponentType<any>
}

// Componente de fallback padrão
const DefaultFallback = () => (
  <div className="flex items-center justify-center p-8">
    <Loader2 className="h-6 w-6 animate-spin text-primary" />
    <span className="ml-2 text-sm text-muted-foreground">Carregando...</span>
  </div>
)

// Sistema de prioridades para prefetch
const PRIORITY_WEIGHTS = {
  high: 1,
  medium: 0.5,
  low: 0.1
}

// Classe para gerenciar lazy loading inteligente
class SmartLazyLoader {
  private static instance: SmartLazyLoader
  private preloadQueue: Array<{ config: LazyLoadConfig; priority: number }> = []
  private isProcessing = false

  static getInstance(): SmartLazyLoader {
    if (!SmartLazyLoader.instance) {
      SmartLazyLoader.instance = new SmartLazyLoader()
    }
    return SmartLazyLoader.instance
  }

  // Carregar componente com cache
  loadComponent(config: LazyLoadConfig): ComponentType<any> {
    const { name, importFn } = config

    // Já totalmente resolvido (ex.: via prefetch) — usar o componente real.
    if (componentCache.has(name)) {
      return componentCache.get(name)!
    }

    // Reutilizar o mesmo wrapper lazy entre renders em vez de criar (ou
    // lançar uma promise) a cada chamada — isso é o que quebrava o Suspense.
    if (lazyComponentCache.has(name)) {
      return lazyComponentCache.get(name)!
    }

    // Criar componente lazy
    const LazyComponent = lazy(async () => {
      try {
        const promise = importFn().then(module => {
          componentCache.set(name, module.default)
          loadingCache.delete(name)
          return module
        })

        loadingCache.set(name, promise as unknown as Promise<ComponentType<any>>)
        return promise
      } catch (error) {
        loadingCache.delete(name)
        throw error
      }
    })

    lazyComponentCache.set(name, LazyComponent)

    // Adicionar à fila de prefetch se configurado
    if (config.preload) {
      this.addToPreloadQueue(config)
    }

    return LazyComponent
  }

  // Adicionar à fila de prefetch
  addToPreloadQueue(config: LazyLoadConfig) {
    const priority = PRIORITY_WEIGHTS[config.priority || 'medium']
    this.preloadQueue.push({ config, priority })
    
    // Ordenar por prioridade
    this.preloadQueue.sort((a, b) => b.priority - a.priority)
    
    // Processar fila se não estiver processando
    if (!this.isProcessing) {
      this.processPreloadQueue()
    }
  }

  // Processar fila de prefetch
  private async processPreloadQueue() {
    if (this.isProcessing || this.preloadQueue.length === 0) return

    this.isProcessing = true

    // 07/10/2026: o prefetch arrancava logo ao carregar o App e disputava a
    // rede com a página que a pessoa abriu (no telemóvel o conteúdo principal
    // da página inicial só aparecia aos 8,4s). Só começa depois do 'load' e
    // de uma pausa, quando a página atual já está visível.
    await esperarPaginaCarregada()

    while (this.preloadQueue.length > 0) {
      const { config } = this.preloadQueue.shift()!
      
      try {
        // Prefetch do componente
        await this.prefetchComponent(config)
        
        // Aguardar um pouco para não sobrecarregar
        await new Promise(resolve => setTimeout(resolve, 100))
      } catch (error) {
        console.warn(`Erro ao fazer prefetch de ${config.name}:`, error)
      }
    }

    this.isProcessing = false
  }

  // Fazer prefetch de um componente
  private async prefetchComponent(config: LazyLoadConfig) {
    const { name, importFn } = config

    // Verificar se já está carregado
    if (componentCache.has(name) || loadingCache.has(name)) {
      return
    }

    try {
      // Carregar em background
      const promise = importFn().then(module => {
        const component = module.default
        componentCache.set(name, component)
        return component
      })

      // Adicionar à cache de loading
      loadingCache.set(name, promise)
      
      // Aguardar carregamento
      await promise
    } catch (error) {
      loadingCache.delete(name)
      throw error
    }
  }

  // Prefetch baseado em navegação
  prefetchOnNavigation(path: string) {
    const routeComponents = this.getRouteComponents(path)
    
    routeComponents.forEach(config => {
      this.addToPreloadQueue({ ...config, priority: 'high' })
    })
  }

  // Obter componentes baseados na rota
  private getRouteComponents(path: string): LazyLoadConfig[] {
    const routeMap: Record<string, LazyLoadConfig[]> = {
      '/servicos': [
        {
          name: 'ServicosPage',
          importFn: () => import('../pages/Servicos'),
          priority: 'high'
        }
      ],
      '/categorias': [
        {
          name: 'CategoriasPage',
          importFn: () => import('../pages/Explore'),
          priority: 'high'
        }
      ]
    }

    // Encontrar rota correspondente
    for (const [route, components] of Object.entries(routeMap)) {
      if (path.startsWith(route)) {
        return components
      }
    }

    return []
  }

  // Limpar cache
  clearCache() {
    componentCache.clear()
    loadingCache.clear()
    lazyComponentCache.clear()
  }

  // Obter estatísticas do cache
  getCacheStats() {
    return {
      loadedComponents: componentCache.size,
      loadingComponents: loadingCache.size,
      preloadQueue: this.preloadQueue.length
    }
  }
}

// Instância global
export const smartLazyLoader = SmartLazyLoader.getInstance()

// Hook para lazy loading inteligente
export function useSmartLazyLoad(config: LazyLoadConfig) {
  const Component = smartLazyLoader.loadComponent(config)
  
  return {
    Component,
    prefetch: () => smartLazyLoader.addToPreloadQueue(config)
  }
}

// Componente wrapper com Suspense
export function LazyComponentWrapper({ 
  config, 
  fallback,
  ...props 
}: { 
  config: LazyLoadConfig
  fallback?: ComponentType<any>
} & Record<string, any>) {
  const { Component } = useSmartLazyLoad(config)
  const FallbackComponent = fallback || DefaultFallback

  return (
    <Suspense fallback={<FallbackComponent />}>
      <Component {...props} />
    </Suspense>
  )
}

// Função para prefetch baseado em hover
export function usePrefetchOnHover(config: LazyLoadConfig) {
  const prefetch = () => smartLazyLoader.addToPreloadQueue(config)

  return {
    onMouseEnter: prefetch,
    onFocus: prefetch
  }
}

// Função para prefetch baseado em visibilidade
export function usePrefetchOnVisible(config: LazyLoadConfig, threshold = 0.1) {
  const prefetch = () => smartLazyLoader.addToPreloadQueue(config)

  return {
    onIntersect: (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          prefetch()
        }
      })
    },
    threshold
  }
}

// Exportar funções utilitárias
export {
    componentCache, DefaultFallback, loadingCache
}

